import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Form,
  FormBuilder,
  FormConfig,
  UploadOrBuildManually
} from '@120wateraudit/form-builder-components';
import { Wizard, WizardHandle } from '@120wateraudit/waterworks';
import { useNavigate } from 'react-router-dom';
import { toastError, toastSuccess } from 'src/utils/toast';
import { SUBMISSION_PERIOD_TEMPLATES_PATHS } from '../paths';
import MetadataStep, { MetadataErrors } from './MetadataStep';
import PreviewStep from './PreviewStep';
import styled from 'styled-components';

const getMetaErrors = (
  meta: Omit<Form, 'config'>
): MetadataErrors => {
  const errors: MetadataErrors = {};
  if (!meta.title.trim()) errors.title = 'Required';
  if (!meta.description?.trim()) errors.description = 'Required';
  if (!meta.subCategory) errors.subCategory = 'Required';
  return errors;
};

const SourceStepContainer = styled.div`
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
`;

const BuilderStepContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const SubmissionPeriodTemplateWizard: React.FC<{
  token: string;
  edit?: boolean;
  existingForm?: Form
  saveForm: (form: Form) => Promise<Form>;
  isSaving: boolean;
}> = ({ token, edit = false, existingForm, saveForm, isSaving }) => {
  const navigate = useNavigate();
  const wizardRef = useRef<WizardHandle>(null);

  const [formMeta, setFormMeta] = useState<Omit<Form, 'config'>>({
    id: existingForm?.id ?? '',
    title: existingForm?.title ?? '',
    description: existingForm?.description ?? '',
    category: existingForm?.category ?? 'Monthly Operating Report',
  });

  const [formConfig, setFormConfig] = useState<FormConfig | undefined>(
    existingForm?.config
  );

  const [showMetaErrors, setShowMetaErrors] = useState(false);

  const metaErrors = useMemo(() => getMetaErrors(formMeta), [formMeta]);
  const isMetaValid = Object.keys(metaErrors).length === 0;

  const handleSourceComplete = useCallback(
    (result: FormConfig | null): void => {
      setFormConfig(result ?? undefined);
      void wizardRef.current?.goForward();
    },
    []
  );

  const beforeNext = useCallback((): boolean => {
    if (!isMetaValid) {
      setShowMetaErrors(true);
      return false;
    }
    return true;
  }, [isMetaValid]);

  const handleComplete = async (): Promise<void> => {
    if (isSaving) return;
    if (!formConfig) {
      toastError("No form content to save.");
      return;
    }

    // this shouldn't be possible unless something goes wrong
    if (!isMetaValid) {
      toastError("Missing required metadata fields.");
      return;
    }

    try {
      await saveForm({ ...formMeta, config: formConfig });
      toastSuccess(edit ? 'Form updated' : 'Form created');
      navigate(SUBMISSION_PERIOD_TEMPLATES_PATHS.index);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : `Failed to ${edit ? 'update' : 'create'} form.`;
      toastError(message);
    }
  };

  return (
    <Wizard
      ref={wizardRef}
      title={edit ? "Edit Form" : "New Form"}
      onClose={() => {
        navigate(SUBMISSION_PERIOD_TEMPLATES_PATHS.index);
      }}
      onComplete={() => {
        void handleComplete();
      }}
      beforeNext={beforeNext}
    >
      <Wizard.Step label="Metadata" sub="Name, description, type">
        <MetadataStep
          formMeta={formMeta}
          errors={showMetaErrors ? metaErrors : undefined}
          onChange={setFormMeta}
        />
      </Wizard.Step>

      {!edit && (
        <Wizard.Step label="Source" sub="Upload or build manually">
          <SourceStepContainer>
            <UploadOrBuildManually
              token={token}
              onComplete={handleSourceComplete}
            />
          </SourceStepContainer>
        </Wizard.Step>
      )}

      <Wizard.Step label="Form Builder" sub="Build the form">
        <BuilderStepContainer>
          <FormBuilder initialConfig={formConfig} onChange={setFormConfig} />
        </BuilderStepContainer>
      </Wizard.Step>

      <Wizard.Step label="Preview" sub="Review & save" forwardLabel="Save Form">
        <PreviewStep config={formConfig} />
      </Wizard.Step>
    </Wizard>
  );
};
